/**
 * @file memory.ts
 * @description Память групп настроек на устройстве (phase19.16, SB122;
 * uiux-settings-groups §1): какие группы панели свёрнуты.
 *
 * Ключ `commora.settings-groups.<панель>`, значение — JSON:
 *  - `collapsed` — от 768: группы, которые человек свернул;
 *  - `expanded` — от 768: группы «свёрнутые по умолчанию», которые он раскрыл;
 *  - `last` — до 768: последняя открытая группа; `null` — свернул все;
 *    поля нет — открыта первая.
 * Все чтения и записи — в try/catch: хранилище может быть недоступно
 * (приватный режим, запрет сайта) — тогда панель просто не помнит.
 */

/** Сохранённое состояние групп одной панели. */
export interface SettingsGroupsMemory {
  collapsed: string[];
  expanded: string[];
  last?: string | null;
}

export const EMPTY_MEMORY: SettingsGroupsMemory = { collapsed: [], expanded: [] };

/** Ключ памяти панели: `commora.settings-groups.footer`. */
export function settingsGroupsKey(panel: string): string {
  return `commora.settings-groups.${panel}`;
}

const isStringList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");

/** Память панели; испорченная или недоступная — пустая. */
export function readMemory(panel: string): SettingsGroupsMemory {
  try {
    const raw = window.localStorage.getItem(settingsGroupsKey(panel));
    if (!raw) return EMPTY_MEMORY;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY_MEMORY;
    const { collapsed, expanded, last } = parsed as Record<string, unknown>;
    const memory: SettingsGroupsMemory = {
      collapsed: isStringList(collapsed) ? collapsed : [],
      expanded: isStringList(expanded) ? expanded : [],
    };
    if (typeof last === "string" || last === null) memory.last = last;
    return memory;
  } catch {
    return EMPTY_MEMORY;
  }
}

export function writeMemory(panel: string, memory: SettingsGroupsMemory): void {
  try {
    window.localStorage.setItem(settingsGroupsKey(panel), JSON.stringify(memory));
  } catch {
    // Хранилище недоступно — состояние живёт до перезагрузки.
  }
}

/** От 768: открыта ли группа. */
export function isOpenWide(id: string, memory: SettingsGroupsMemory, defaultCollapsed: readonly string[]): boolean {
  if (memory.collapsed.includes(id)) return false;
  return !defaultCollapsed.includes(id) || memory.expanded.includes(id);
}

/** От 768: раскрыть или свернуть группу. */
export function setOpenWide(
  memory: SettingsGroupsMemory,
  id: string,
  open: boolean,
  defaultCollapsed: readonly string[],
): SettingsGroupsMemory {
  const collapsed = memory.collapsed.filter((x) => x !== id);
  const expanded = memory.expanded.filter((x) => x !== id);
  if (!open) collapsed.push(id);
  else if (defaultCollapsed.includes(id)) expanded.push(id);
  return { ...memory, collapsed, expanded };
}

/** От 768: раскрыть все (и свёрнутые по умолчанию). */
export function expandAllWide(memory: SettingsGroupsMemory, ids: readonly string[]): SettingsGroupsMemory {
  return { ...memory, collapsed: [], expanded: [...new Set([...memory.expanded, ...ids])] };
}

/**
 * До 768: какая группа открыта. Последняя открытая, если она есть в панели;
 * иначе первая не «свёрнутая по умолчанию»; `last: null` — ни одной.
 */
export function openNarrowId(memory: SettingsGroupsMemory, ids: readonly string[], defaultCollapsed: readonly string[]): string | null {
  if (memory.last === null) return null;
  if (memory.last !== undefined && ids.includes(memory.last)) return memory.last;
  return ids.find((id) => !defaultCollapsed.includes(id)) ?? ids[0] ?? null;
}
